import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dateInputValue, formatDateTime, parseUtcTimestamp } from '../../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../../shared/DateTimeProvider';
import { Archive } from 'lucide-react';
import { tenantApi, type NotificationInboxItem } from '../../services/tenantApi';
import { filterStandaloneNotifications } from './notificationViewModel';
import { NotificationDetail } from './NotificationDetail';
import { NotificationDeliveryIncidentsPanel, type IncidentHealth } from './NotificationDeliveryIncidentsPanel';
import { timezoneDateRangeFilters } from '../../shared/timezoneDateRange';
import './NotificationsView.css';

type NotificationTimeRange = 'all_time' | 'last_15_mins' | 'last_1_hour' | 'last_12_hours' | 'last_24_hours' | 'last_7_days' | 'last_30_days' | 'custom';

interface NotificationsViewProps {
  reloadToken?: number;
  onChanged?: () => void;
  canViewIncidents?: boolean;
  isTenantAdmin?: boolean;
  canViewEmailHistory?: boolean;
  canManageIncidents?: boolean;
}

export const NotificationsView: React.FC<NotificationsViewProps> = ({ reloadToken = 0, onChanged, canViewIncidents = false, isTenantAdmin = false, canViewEmailHistory = isTenantAdmin, canManageIncidents = isTenantAdmin }) => {
  const { timezone } = useDateTimePreferences();
  const [items, setItems] = useState<NotificationInboxItem[]>([]);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [status, setStatus] = useState('open');
  const [severity, setSeverity] = useState('all');
  const [service, setService] = useState('all');
  const [services, setServices] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<NotificationTimeRange>('all_time');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailReload, setDetailReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [showIncidents, setShowIncidents] = useState(false);
  const [incidentHealth, setIncidentHealth] = useState<IncidentHealth>('checking');
  const requestSequence = useRef(0);

  const resolveTimeWindow = (): { startUtc?: string; endUtc?: string } => {
    const now = new Date();
    const fromDuration = (durationMs: number) => ({
      startUtc: new Date(now.getTime() - durationMs).toISOString(),
      endUtc: now.toISOString(),
    });
    switch (timeRange) {
      case 'last_15_mins': return fromDuration(15 * 60 * 1000);
      case 'last_1_hour': return fromDuration(60 * 60 * 1000);
      case 'last_12_hours': return fromDuration(12 * 60 * 60 * 1000);
      case 'last_24_hours': return fromDuration(24 * 60 * 60 * 1000);
      case 'last_7_days': return fromDuration(7 * 24 * 60 * 60 * 1000);
      case 'last_30_days': return fromDuration(30 * 24 * 60 * 60 * 1000);
      case 'custom': return timezoneDateRangeFilters(customStartDate, customEndDate, timezone) ?? {};
      default: return {};
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(search.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setLoadError('');
    setDetailReload(value => value + 1);
    try {
      const page = await tenantApi.getInboundNotificationsPage({
        take: 50, unreadOnly, status,
        severity: severity === 'all' ? undefined : severity,
        service: service === 'all' ? undefined : service,
        search: searchQuery || undefined,
        ...resolveTimeWindow(),
      });
      if (sequence === requestSequence.current) {
        setItems(filterStandaloneNotifications(page.items));
        setNextCursor(page.nextCursor);
        setTotal(page.total);
        setServices(page.availableServices ?? []);
        setStatuses(page.availableStatuses ?? []);
      }
    } catch {
      if (sequence === requestSequence.current) setLoadError('Failed to load notifications. Please retry.');
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [unreadOnly, status, severity, service, searchQuery, timeRange, customStartDate, customEndDate, timezone]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore || loading || busy) return;
    const sequence = ++requestSequence.current;
    setLoadingMore(true);
    setLoadError('');
    try {
      const page = await tenantApi.getInboundNotificationsPage({
        take: 50, cursor: nextCursor, unreadOnly, status,
        severity: severity === 'all' ? undefined : severity,
        service: service === 'all' ? undefined : service,
        search: searchQuery || undefined,
        ...resolveTimeWindow(),
      });
      if (sequence === requestSequence.current) {
        setItems(current => {
          const known = new Set(current.map(item => item.notificationId));
          return [...current, ...page.items.filter(item => !known.has(item.notificationId))];
        });
        setNextCursor(page.nextCursor);
        setTotal(page.total);
      }
    } catch {
      if (sequence === requestSequence.current) setLoadError('Failed to load more notifications. Please retry.');
    } finally {
      if (sequence === requestSequence.current) setLoadingMore(false);
    }
  };

  useEffect(() => {
    void load();
    return () => { requestSequence.current += 1; };
  }, [load, reloadToken]);

  const visible = filterStandaloneNotifications(items, searchQuery, unreadOnly, status, severity, service);
  const selected = items.find(item => item.notificationId === selectedId);
  const closable = visible.filter(item => item.status?.toLowerCase() !== 'closed');
  const disabled = busy || loading || loadingMore || Boolean(loadError);

  const changeTimeRange = (next: NotificationTimeRange) => {
    setTimeRange(next);
    if (next !== 'custom') return;
    const today = dateInputValue(new Date(), { timezone });
    setCustomStartDate(value => value || today);
    setCustomEndDate(value => value || today);
  };

  const runAction = async (action: () => Promise<void>) => {
    setBusy(true);
    setActionError('');
    try {
      await action();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Notification action failed. Please retry.');
    } finally {
      onChanged?.();
      await load();
      setBusy(false);
    }
  };

  const closeNotifications = (entries: NotificationInboxItem[]) => {
    const message = entries.length === 1 ? `Close Notification "${entries[0].title || 'Notification'}"?`
      : `Close ${entries.length} visible notifications?`;
    if (!window.confirm(message)) return;
    void runAction(async () => {
      const results = await Promise.allSettled(entries.map(item => tenantApi.closeInboundNotification(item.notificationId)));
      const failed = results.filter(result => result.status === 'rejected');
      if (failed.length) {
        const error = (failed[0] as PromiseRejectedResult).reason;
        throw new Error(`Failed to close ${failed.length} notification${failed.length === 1 ? '' : 's'}. ${error instanceof Error ? error.message : 'Please retry.'}`);
      }
    });
  };

  return (
    <section className="notifications-view" aria-labelledby="notifications-heading" aria-busy={loading || loadingMore || busy}>
      <div className="notifications-view-controls">
      <header className="notifications-view-heading">
        <h2 id="notifications-heading">Notifications</h2>
        <div className="notifications-view-heading-actions">
          {canViewIncidents && (
            <button type="button" className="notifications-incidents-toggle"
              aria-expanded={showIncidents} aria-controls="notification-delivery-incidents"
              onClick={() => setShowIncidents(value => !value)}>
              {showIncidents ? 'Hide' : 'Show'} email delivery incidents
              {incidentHealth !== 'clear' && (
                <span className={`notifications-incidents-chip ${incidentHealth}`} role="status">
                  {incidentHealth === 'active' ? 'Active issues'
                    : incidentHealth === 'unavailable' ? 'Status unavailable' : 'Checking'}
                </span>
              )}
            </button>
          )}
          <span className="notifications-view-count">{visible.length} visible / {items.length} loaded / {total} total</span>
        </div>
      </header>
      <div className="notifications-view-toolbar">
        <input type="search" aria-label="Search notifications" placeholder="Search notifications..." value={search}
          onChange={(event) => setSearch(event.target.value)} />
        <select aria-label="Notification status" value={status} onChange={event => setStatus(event.target.value)}>
          <option value="open">Open notifications</option><option value="all">All statuses</option>
          {statuses.filter(value => value.toLowerCase() !== 'open').map(value => <option key={value} value={value.toLowerCase()}>{value}</option>)}
        </select>
        <select aria-label="Notification severity" value={severity} onChange={event => setSeverity(event.target.value)}>
          <option value="all">All severities</option>
          {['critical', 'high', 'medium', 'low', 'info'].map(value => <option key={value} value={value}>{value}</option>)}
        </select>
        <select aria-label="Notification service" value={service} onChange={event => setService(event.target.value)}>
          <option value="all">All services</option>
          {services.map(value => <option key={value} value={value}>{value}</option>)}
        </select>
        <select aria-label="Notification time range" value={timeRange} onChange={event => changeTimeRange(event.target.value as NotificationTimeRange)}>
          <option value="all_time">All time</option>
          <option value="last_15_mins">Last 15 mins</option>
          <option value="last_1_hour">Last 1 hour</option>
          <option value="last_12_hours">Last 12 hours</option>
          <option value="last_24_hours">Last 24 hours</option>
          <option value="last_7_days">Last 7 days</option>
          <option value="last_30_days">Last 30 days</option>
          <option value="custom">Custom range</option>
        </select>
        {timeRange === 'custom' && <div className="notifications-custom-range">
          <label>Start<input type="date" aria-label="Notification start date" value={customStartDate}
            max={customEndDate || undefined} onChange={event => setCustomStartDate(event.target.value)} /></label>
          <label>End<input type="date" aria-label="Notification end date" value={customEndDate}
            min={customStartDate || undefined} onChange={event => setCustomEndDate(event.target.value)} /></label>
        </div>}
        <label><input type="checkbox" checked={unreadOnly} onChange={(event) => setUnreadOnly(event.target.checked)} /> Unread only</label>
        <button type="button" className="notifications-icon-button" title="Refresh notifications" aria-label="Refresh notifications"
          disabled={loading || busy} onClick={() => { setActionError(''); void load(); }}>&#8635;</button>
        <button type="button" className="notifications-clear-visible" title="Close visible notifications" disabled={disabled || closable.length === 0}
          onClick={() => closeNotifications(closable)}>
          Close visible
        </button>
      </div>
      </div>
      <div className="notifications-results">
      {canViewIncidents && (
        <div id="notification-delivery-incidents" hidden={!showIncidents}>
          <NotificationDeliveryIncidentsPanel canManage={canManageIncidents} reloadToken={reloadToken}
            onHealthChanged={setIncidentHealth} />
        </div>
      )}
      {loadError || actionError ? <div className="notifications-view-error" role="alert">{loadError || actionError}</div> : null}
      <div className={`notifications-split ${selected ? 'has-selection' : ''}`}>
      <div className="notifications-list-pane">
      {loading ? <p className="notifications-view-state" role="status">Loading notifications...</p> : null}
      {!loading && !loadError && visible.length === 0 ? (
        <p className="notifications-view-state">{search.trim() || unreadOnly || status !== 'all' || severity !== 'all' ? 'No matching notifications.' : 'No standalone notifications.'}</p>
      ) : null}
      <ul className="notifications-view-list">
        {visible.map((item) => {
          const date = parseUtcTimestamp(item.createdAt);
          return (
            <li key={item.notificationId} className={`notifications-view-row ${item.isUnread ? 'is-unread' : ''} ${selectedId === item.notificationId ? 'is-selected' : ''}`}>
              <button type="button" className="notifications-view-content" aria-label={`View details for ${item.title || 'notification'}`}
                aria-pressed={selectedId === item.notificationId} onClick={() => setSelectedId(item.notificationId)}>
                <div className="notifications-view-title"><strong>{item.title || item.service || 'Notification'}</strong>
                  {item.isUnread ? <span className="notifications-unread-label">Unread</span> : null}</div>
                {item.subtitle ? <span className="notifications-row-subtitle">{item.subtitle}</span> : null}
                <div className="notifications-view-meta">
                  <span className={`notifications-severity severity-${item.severity || 'info'}`}>{item.severity || 'info'}</span>
                  {item.status ? <span>{item.status}</span> : null}
                  {item.service ? <span>{item.service}</span> : null}
                  {!Number.isNaN(date.getTime()) ? <time dateTime={date.toISOString()}>{formatDateTime(item.createdAt)}</time> : null}
                </div>
              </button>
              <div className="notifications-view-actions">
                <button type="button" className="notifications-icon-button" title="Mark as read" aria-label={`Mark ${item.title || 'notification'} as read`}
                  disabled={disabled || !item.isUnread} onClick={() => void runAction(() => tenantApi.markNotificationRead(item.notificationId))}>&#10003;</button>
                <button type="button" className="notifications-icon-button" title="Close Notification" aria-label={`Close Notification: ${item.title || 'Notification'}`}
                  disabled={disabled || item.status?.toLowerCase() === 'closed'} onClick={() => closeNotifications([item])}><Archive size={16} /></button>
              </div>
            </li>
          );
        })}
      </ul>
      {nextCursor && <button type="button" className="notifications-load-more" disabled={disabled}
        onClick={() => void loadMore()}>{loadingMore ? 'Loading…' : `Load more (${Math.max(0, total - items.length)} remaining)`}</button>}
      </div>
      {selected ? <NotificationDetail key={selected.notificationId} item={selected} reloadToken={detailReload}
        isTenantAdmin={isTenantAdmin} canViewEmailHistory={canViewEmailHistory} onClose={() => setSelectedId(null)} /> : <div className="notifications-detail-empty">Select a notification</div>}
      </div>
      </div>
    </section>
  );
};